import { Input, type InputProps } from "@chakra-ui/react";
import type { ElementType, KeyboardEvent } from "react";
import { forwardRef, useCallback } from "react";
import CurrencyInput from "react-currency-input-field";

export type GuidedBudgetCurrencyInputProps = {
  amount: number | null | undefined;
  onCommit: (next: number | null) => void;
  /** Defaults to Chakra `Input` (e.g. unstyled summary strip). Use `WhiteBgInput` for phase rows. */
  customInput?: ElementType;
} & Omit<InputProps, "value" | "defaultValue" | "onChange" | "onBlur">;

/**
 * Guided budget amount editor using `react-currency-input-field` (same stack as `BudgetSelector`).
 * Locale separators only — no `$` in the field when the shell renders `$` beside the input.
 */
export const GuidedBudgetCurrencyInput = forwardRef<
  HTMLInputElement,
  GuidedBudgetCurrencyInputProps
>(function GuidedBudgetCurrencyInput(
  { amount, onCommit, customInput = Input, onKeyDown, ...inputProps },
  ref
) {
  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        (e.target as HTMLInputElement).blur();
      }
      onKeyDown?.(e);
    },
    [onKeyDown]
  );

  return (
    // @ts-ignore Chakra `InputProps` spread to `CurrencyInput` (forwarded to `customInput`; see BudgetSelector).
    <CurrencyInput
      ref={ref}
      allowNegativeValue={false}
      customInput={customInput}
      decimalsLimit={2}
      disableGroupSeparators
      intlConfig={{ locale: "en-US" }}
      placeholder="0.00"
      value={amount != null && Number.isFinite(amount) ? String(amount) : undefined}
      onValueChange={(_value, _name, values) => {
        onCommit(values?.float ?? null);
      }}
      onKeyDown={handleKeyDown}
      {...inputProps}
    />
  );
});

GuidedBudgetCurrencyInput.displayName = "GuidedBudgetCurrencyInput";
